import React, { useState, useEffect } from 'react';
import {
  Settings, RefreshCw, Check, X, Loader2, ShieldCheck, AlertTriangle, BarChart3, Table2, Copy,
} from 'lucide-react';
import { api } from '../../lib/api';
import { Field } from '../Shared';

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [stats, setStats] = useState(null);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [testState, setTestState] = useState(null);
  const [testMsg, setTestMsg] = useState('');
  const [confirmPurge, setConfirmPurge] = useState(false);
  const [purgeMsg, setPurgeMsg] = useState('');
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get('/api/admin/settings').then(setSettings).catch((e) => setErr(e.message));
    api.get('/api/admin/stats').then(setStats).catch(() => {});
  }, []);

  const set = (k) => (e) => {
    const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
    setSettings({ ...settings, [k]: value });
    setDirty(true);
    setSaved(false);
  };

  // Les identifiants de classeurs sont imbriqués dans la réponse mais s'envoient à plat.
  const setSheet = (k) => (e) => {
    setSettings({ ...settings, google_sheets: { ...settings.google_sheets, [k]: e.target.value } });
    setDirty(true);
    setSaved(false);
  };

  const save = async () => {
    setSaving(true);
    setErr('');
    try {
      const data = await api.patch('/api/admin/settings', {
        guest_quota: settings.guest_quota,
        global_quota: settings.global_quota,
        kill_switch: settings.kill_switch,
        voice_id: settings.voice_id,
        openai_model: settings.openai_model,
        sheet_anecdotes_id: settings.google_sheets.sheet_anecdotes_id || '',
        sheet_questionnaire_id: settings.google_sheets.sheet_questionnaire_id || '',
      });
      setSettings(data);
      setDirty(false);
      setSaved(true);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTestState('testing');
    setTestMsg('');
    try {
      const data = await api.post('/api/admin/test_engine');
      setTestState('ok');
      setTestMsg(data.reply);
    } catch (e) {
      setTestState('err');
      setTestMsg(e.message || 'Échec.');
    }
  };

  const purge = async () => {
    if (!confirmPurge) {
      setConfirmPurge(true);
      setTimeout(() => setConfirmPurge(false), 4000);
      return;
    }
    setConfirmPurge(false);
    try {
      const data = await api.post('/api/admin/purge');
      setPurgeMsg(`Purge effectuée : ${data.purged.messages} messages, ${data.purged.guest_sessions} sessions, cache audio vidé.`);
      api.get('/api/admin/stats').then(setStats).catch(() => {});
    } catch (e) {
      setErr(e.message);
    }
  };

  if (!settings) return <div className="empty">{err || 'Chargement…'}</div>;

  return (
    <div className="admin-grid">
      <div className="panel span2">
        <h3 className="panel-h"><Settings size={17} /> Moteur IA & voix</h3>
        <div className="engine-chips">
          <span className={`engine-chip${settings.engines.openai_configured ? ' ok' : ''}`}>
            {settings.engines.openai_configured ? <Check size={13} /> : <X size={13} />} OpenAI — raisonnement
          </span>
          <span className={`engine-chip${settings.engines.elevenlabs_configured ? ' ok' : ''}`}>
            {settings.engines.elevenlabs_configured ? <Check size={13} /> : <X size={13} />} ElevenLabs — voix clonée
          </span>
        </div>
        <p className="panel-p">
          Les clés API vivent dans les credentials Rails du serveur (jamais dans le navigateur).
          Ici, on ne règle que les paramètres d'exécution.
        </p>
        <div className="settings-row">
          <Field label="Modèle OpenAI" value={settings.openai_model} onChange={set('openai_model')} placeholder="gpt-4o-mini" />
          <Field label="Voice ID ElevenLabs (voix clonée)" value={settings.voice_id} onChange={set('voice_id')} placeholder="21m00Tcm4TlvDq8ikWAM" />
        </div>
        <div className="settings-row">
          <Field label="Quota messages par invité" type="number" value={settings.guest_quota} onChange={set('guest_quota')} />
          <Field label="Plafond global de la soirée" type="number" value={settings.global_quota} onChange={set('global_quota')} />
        </div>
        <div className={`toggle-row${settings.kill_switch ? ' warn' : ''}`}>
          <div>
            <div className="toggle-label">Kill switch</div>
            <div className="toggle-sub">Coupe immédiatement toutes les réponses IA pour les invités.</div>
          </div>
          <button
            className={`switch${settings.kill_switch ? ' on' : ''}`}
            onClick={() => { setSettings({ ...settings, kill_switch: !settings.kill_switch }); setDirty(true); setSaved(false); }}
            aria-label="Kill switch"
          />
        </div>
        {err && <div className="test err">{err}</div>}
        <div className="save-bar">
          <button className="btn-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? <><Loader2 size={15} className="spin" /> Enregistrement…</> : saved ? <><Check size={15} /> Enregistré</> : 'Enregistrer les réglages'}
          </button>
          <button className="btn-ghost" onClick={test} disabled={testState === 'testing'}>
            {testState === 'testing' ? <><Loader2 size={15} className="spin" /> Test en cours…</> : <><RefreshCw size={15} /> Tester la connexion</>}
          </button>
        </div>
        {testState === 'ok' && <div className="test ok"><Check size={15} /> Connecté — « {testMsg} »</div>}
        {testState === 'err' && <div className="test err"><X size={15} /> {testMsg}</div>}
      </div>

      <div className="panel span2">
        <h3 className="panel-h"><Table2 size={17} /> Google Sheets — synchronisation des questionnaires</h3>
        {settings.google_sheets.configured ? (
          <>
            <p className="panel-p">
              Partagez chaque classeur de réponses avec l'adresse ci-dessous (bouton <b>Partager</b> dans
              Google Sheets, accès <b>Lecteur</b> suffit), puis collez son identifiant. L'onglet Import
              proposera alors un bouton de rechargement direct, sans passer par un fichier.
            </p>
            <div className="secret-box">
              <span className="secret-label">Compte de service — à qui partager les classeurs</span>
              <div className="secret-val">
                <code>{settings.google_sheets.service_account_email}</code>
                <button
                  className="mini-btn"
                  onClick={() => navigator.clipboard?.writeText(settings.google_sheets.service_account_email)}
                >
                  <Copy size={14} />
                </button>
              </div>
            </div>
            <div className="settings-row">
              <Field
                label="Identifiant du classeur « anecdotes »"
                value={settings.google_sheets.sheet_anecdotes_id || ''}
                onChange={setSheet('sheet_anecdotes_id')}
                placeholder="1AbC…"
              />
              <Field
                label="Identifiant du classeur « questionnaire »"
                value={settings.google_sheets.sheet_questionnaire_id || ''}
                onChange={setSheet('sheet_questionnaire_id')}
                placeholder="1XyZ…"
              />
            </div>
            <div className="hint left">
              L'identifiant se lit dans l'URL du classeur, entre <code>/d/</code> et <code>/edit</code> :
              docs.google.com/spreadsheets/d/<b>CET-IDENTIFIANT</b>/edit
            </div>
          </>
        ) : (
          <div className="sec-warn slim">
            <ShieldCheck size={15} />
            Aucun compte de service Google configuré. Renseignez la variable d'environnement
            <code> GOOGLE_SERVICE_ACCOUNT_JSON</code> sur le serveur pour activer la synchronisation
            directe des questionnaires. L'import par fichier reste disponible sans cela.
          </div>
        )}
      </div>

      <div className="panel span2">
        <h3 className="panel-h"><BarChart3 size={17} /> Statistiques & RGPD</h3>
        {stats && (
          <div className="stats-grid">
            <div className="stat-tile"><div className="stat-val">{stats.sessions_count}</div><div className="stat-lab">sessions invités</div></div>
            <div className="stat-tile"><div className="stat-val">{stats.messages_count}</div><div className="stat-lab">questions posées (plafond {stats.global_quota})</div></div>
            <div className="stat-tile"><div className="stat-val">{stats.offline_messages_count}</div><div className="stat-lab">réponses hors ligne</div></div>
            <div className="stat-tile"><div className="stat-val">{stats.average_latency_ms ? `${stats.average_latency_ms} ms` : '—'}</div><div className="stat-lab">latence moyenne</div></div>
          </div>
        )}
        <div className="sec-warn slim">
          <ShieldCheck size={15} />
          Purge RGPD : supprime tous les messages, les sessions invités et le cache audio.
          La suppression de la voix clonée côté ElevenLabs se fait depuis leur console.
        </div>
        <div className="save-bar">
          <button className={`btn-ghost danger${confirmPurge ? ' armed' : ''}`} onClick={purge}>
            <AlertTriangle size={15} /> {confirmPurge ? 'Cliquer à nouveau pour confirmer la purge' : 'Purger les données (RGPD)'}
          </button>
          {purgeMsg && <span className="test ok inline"><Check size={14} /> {purgeMsg}</span>}
        </div>
      </div>
    </div>
  );
}
